import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Icon } from "@/lib/icons";

// Small shared pieces of the public home page: one way to open a section,
// one waveform, one error line, two link buttons.

export function HomeSection({
  id,
  children,
  className,
  labelledBy,
}: {
  id?: string;
  children: ReactNode;
  className?: string;
  labelledBy?: string;
}) {
  return (
    <section id={id} aria-labelledby={labelledBy} className={cn("scroll-mt-16 border-t border-border/60", className)}>
      <div className="container mx-auto px-4 py-16 sm:px-6 sm:py-20 lg:py-24">{children}</div>
    </section>
  );
}

export function SectionHeading({
  id,
  eyebrow,
  title,
  className,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn("max-w-2xl", className)}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">{eyebrow}</p>
      <h2 id={id} className="mt-3 text-3xl font-bold leading-[1.1] tracking-[-0.03em] text-foreground sm:text-4xl">
        {title}
      </h2>
      {children}
    </div>
  );
}

/** Bar heights of the signature waveform, in percent of its height. */
const WAVE = [28, 46, 64, 40, 82, 58, 94, 70, 48, 76, 100, 62, 38, 72, 88, 54, 34, 60, 80, 44, 26, 52, 68, 36];

/**
 * The Yodi signature: a row of bars like a voice recording. It breathes
 * slowly (CSS .yodi-wave); the global reduced-motion rule stops it.
 */
export function Waveform({ className, bars = WAVE.length, animated = true }: { className?: string; bars?: number; animated?: boolean }) {
  return (
    <div aria-hidden="true" className={cn("flex items-end gap-[3px] sm:gap-1", animated && "yodi-wave", className)}>
      {WAVE.slice(0, bars).map((height, index) => (
        <span
          key={index}
          className="block w-full min-w-[3px] rounded-full bg-primary"
          style={{ height: `${height}%`, ["--i" as string]: index, opacity: 0.45 + (height / 100) * 0.55 }}
        />
      ))}
    </div>
  );
}

export function ErrorLine({ message, retryLabel, onRetry }: { message: string; retryLabel: string; onRetry: () => void }) {
  return (
    <div role="alert" className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-surface px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
      <span className="flex items-center gap-2">
        <Icon name="alertCircle" className="h-4 w-4 shrink-0 text-danger" />
        {message}
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="interactive inline-flex h-9 w-fit items-center gap-2 rounded-full border border-border px-4 font-medium text-foreground hover:bg-muted/60 focus-ring"
      >
        <Icon name="refreshCw" className="h-3.5 w-3.5" />
        {retryLabel}
      </button>
    </div>
  );
}

const base =
  "interactive inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-[15px] font-semibold focus-ring";

export function PrimaryLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cn(base, "bg-primary text-primary-foreground hover:bg-primary/90", className)}>
      {children}
      <Icon name="arrowRight" className="h-4 w-4" />
    </Link>
  );
}

export function SecondaryLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cn(base, "border border-border bg-background text-foreground hover:bg-muted/60", className)}>
      {children}
    </Link>
  );
}
